import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  FormControlLabel,
  Switch,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getOfferById, createOffer, updateOffer } from '../services/offerService';
import { getOfferTemplateById } from '../services/offerTemplateService';
import categoryService from '../services/categoryService';
import { useAuth } from '../context/AuthContext';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TemplateIcon from '@mui/icons-material/ViewModule';
import CategoryIcon from '@mui/icons-material/Category';
import SubcategorySelector from '../components/SubcategorySelector';
import BrandSelector from '../components/BrandSelector';

const OfferEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isEditing = Boolean(id);
  
  // Check for templateId in URL params
  const searchParams = new URLSearchParams(location.search);
  const templateId = searchParams.get('templateId');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    expirationDate: dayjs().add(7, 'day'), // Default to 1 week from now
    purchaseUrl: '',
    blogLink: '',
    featuredImage: '',
    published: true,
    // Subcategory fields
    category: '',
    subcategoryId: '',
    // Brand field
    brand: null,
    // Template-related fields
    templateId: templateId || null,
    discountPercentage: '',
    discountAmount: '',
    originalPrice: '',
    finalPrice: '',
    features: [],
    tags: [],
    callToAction: '',
    urgencyText: '',
    trafficSources: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [featuresInput, setFeaturesInput] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  
  // Subcategory selection states
  const [showSubcategorySelector, setShowSubcategorySelector] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [step, setStep] = useState(isEditing ? 'form' : 'subcategory');

  const fetchTemplate = useCallback(async (templateId) => {
    try {
      const template = await getOfferTemplateById(templateId);
      setTemplateInfo(template);
      
      // Auto-fill form with template data
      const expirationDate = dayjs().add(template.template.validityDays || 7, 'day');
      
      setFormData(prev => ({
        ...prev,
        title: template.template.title || '',
        description: template.template.description || '',
        expirationDate,
        templateId: template._id,
        discountPercentage: template.template.discountPercentage || '',
        discountAmount: template.template.discountAmount || '',
        originalPrice: template.template.originalPrice || '',
        finalPrice: template.template.finalPrice || '',
        features: template.template.features || [],
        tags: template.template.tags || [],
        callToAction: template.template.callToAction || '',
        urgencyText: template.template.urgencyText || '',
        trafficSources: template.template.trafficSources ? (Array.isArray(template.template.trafficSources) ? template.template.trafficSources : template.template.trafficSources.split(',').map(s => s.trim()).filter(s => s)) : []
      }));
      
      // Set input strings for features and tags
      setFeaturesInput((template.template.features || []).join(', '));
      setTagsInput((template.template.tags || []).join(', '));
      
    } catch (err) {
      setError('Failed to load template data. Please try again.');
      console.error('Error fetching template:', err);
    }
  }, []);

  const fetchOffer = useCallback(async () => {
    try {
      setLoading(true);
      const offer = await getOfferById(id);
      
      // Fetch category info to populate subcategory data
      let categoryInfo = null;
      let subcategoryInfo = null;
      
      if (offer.category) {
        try {
          categoryInfo = await categoryService.getCategoryById(offer.category);
          subcategoryInfo = categoryInfo.subcategories.find(sub => sub._id === offer.subcategoryId);
        } catch (categoryError) {
          console.warn('Failed to fetch category info:', categoryError);
        }
      }
      
      setFormData({
        title: offer.title || '',
        description: offer.description || '',
        expirationDate: dayjs(offer.expirationDate),
        purchaseUrl: offer.purchaseUrl || '',
        blogLink: offer.blogLink || '',
        featuredImage: offer.featuredImage || '',
        published: offer.published !== undefined ? offer.published : true,
        category: offer.category || '',
        subcategoryId: offer.subcategoryId || '',
        brand: offer.brand || null,
        templateId: offer.templateId || null,
        discountPercentage: offer.discountPercentage || '',
        discountAmount: offer.discountAmount || '',
        originalPrice: offer.originalPrice || '',
        finalPrice: offer.finalPrice || '',
        features: offer.features || [],
        tags: offer.tags || [],
        callToAction: offer.callToAction || '',
        urgencyText: offer.urgencyText || '',
        trafficSources: offer.trafficSources ? (Array.isArray(offer.trafficSources) ? offer.trafficSources : offer.trafficSources.split(',').map(s => s.trim()).filter(s => s)) : []
      });
      
      // Set category and subcategory info for display
      if (categoryInfo && subcategoryInfo) {
        setSelectedCategory(categoryInfo);
        setSelectedSubcategory(subcategoryInfo);
      }
      
      // Set input strings for features and tags
      setFeaturesInput((offer.features || []).join(', '));
      setTagsInput((offer.tags || []).join(', '));
      
      setError(null);
    } catch (err) {
      setError('Failed to fetch offer details. You might not have permission to edit this offer.');
      console.error('Error fetching offer:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    if (isEditing) {
      fetchOffer();
    } else if (templateId) {
      setLoading(true);
      fetchTemplate(templateId).finally(() => setLoading(false));
    }
  }, [isAuthenticated, navigate, isEditing, templateId, fetchOffer, fetchTemplate]);

  const handleSubcategorySelect = (category, subcategory) => {
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    
    // Use callback pattern to ensure proper state update
    setFormData(prevFormData => {
      const newFormData = {
        ...prevFormData,
        category: category._id,
        subcategoryId: subcategory._id
      };
      return newFormData;
    });
  };

  // Use useEffect to handle step change after state updates
  useEffect(() => {
    if (selectedCategory && selectedSubcategory && !isEditing && step === 'subcategory') {
      setStep('form');
      setShowSubcategorySelector(false);
    }
  }, [selectedCategory, selectedSubcategory, isEditing, step]);

  const handleChangeSubcategory = () => {
    setShowSubcategorySelector(true);
  };

  const handleChange = (field) => (event) => {
    const value = field === 'published' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      expirationDate: date
    }));
  };

  const handleFeaturesChange = (event) => {
    const value = event.target.value;
    setFeaturesInput(value);
    const featuresArray = value.split(',').map(f => f.trim()).filter(f => f.length > 0);
    setFormData(prev => ({
      ...prev,
      features: featuresArray
    }));
  };

  const handleTagsChange = (event) => {
    const value = event.target.value;
    setTagsInput(value);
    const tagsArray = value.split(',').map(t => t.trim()).filter(t => t.length > 0);
    setFormData(prev => ({
      ...prev,
      tags: tagsArray
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    // Debug logging
    console.log('Validating form with data:', {
      category: formData.category,
      subcategoryId: formData.subcategoryId,
      isEditing
    });
    
    // Check subcategory requirement for new offers
    if (!isEditing && (!formData.category || !formData.subcategoryId)) {
      console.log('Subcategory validation failed:', {
        category: formData.category,
        subcategoryId: formData.subcategoryId
      });
      errors.push('Category and subcategory are required');
    }
    
    if (!formData.title.trim()) {
      errors.push('Title is required');
    }
    
    if (!formData.description.trim()) {
      errors.push('Description is required');
    }
    
    if (!formData.purchaseUrl.trim()) {
      errors.push('Purchase URL is required');
    } else {
      try {
        new URL(formData.purchaseUrl);
      } catch {
        errors.push('Purchase URL must be a valid URL');
      }
    }
    
    if (formData.blogLink && formData.blogLink.trim()) {
      try {
        new URL(formData.blogLink);
      } catch {
        errors.push('Blog link must be a valid URL');
      }
    }
    
    if (!formData.expirationDate) {
      errors.push('Expiration date is required');
    } else if (formData.expirationDate.isBefore(dayjs())) {
      errors.push('Expiration date must be in the future');
    }
    
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    try {
      setSubmitLoading(true);
      setError(null);
      
      const offerData = {
        ...formData,
        expirationDate: formData.expirationDate.toISOString(),
        brand: formData.brand?._id || null
      };

      // Filter out empty string values for ObjectId fields
      if (!offerData.templateId || offerData.templateId === '') {
        delete offerData.templateId;
      }
      if (!offerData.category || offerData.category === '') {
        delete offerData.category;
      }
      if (!offerData.subcategoryId || offerData.subcategoryId === '') {
        delete offerData.subcategoryId;
      }
      if (!offerData.brand) {
        delete offerData.brand;
      }

      let result;
      if (isEditing) {
        result = await updateOffer(id, offerData);
        setSuccess('Offer updated successfully!');
      } else {
        result = await createOffer(offerData);
        setSuccess('Offer created successfully!');
      }

      // Navigate to the offer detail page after a short delay
      setTimeout(() => {
        navigate(`/offers/${result.slug}`);
      }, 1500);

    } catch (err) {
      setError(isEditing ? 'Failed to update offer. Please try again.' : 'Failed to create offer. Please try again.');
      console.error('Error saving offer:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            onClick={() => navigate('/offers')}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{ mb: 2 }}
          >
            Back to Offers
          </Button>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}
          >
            <LocalOfferIcon sx={{ mr: 2, fontSize: 40 }} />
            {isEditing ? 'Edit Offer' : 'Create New Offer'}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {isEditing ? 'Update your offer details' : 'Share an exclusive deal with your audience'}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent sx={{ p: 4 }}>
            {templateInfo && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TemplateIcon sx={{ mr: 1 }} />
                  Using Template: {templateInfo.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {templateInfo.description}
                </Typography>
                <Chip 
                  label={templateInfo.category} 
                  size="small" 
                  sx={{ mt: 1, textTransform: 'capitalize' }}
                />
              </Box>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Subcategory Selection for New Offers */}
              {!isEditing && step === 'subcategory' && (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CategoryIcon sx={{ mr: 1 }} />
                    First, Select a Category for Your Offer
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Choose the category and subcategory where your offer will be displayed.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setShowSubcategorySelector(true)}
                  >
                    Select Category & Subcategory
                  </Button>
                </Paper>
              )}

              {/* Subcategory Display */}
              {(selectedCategory && selectedSubcategory) && (
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <CategoryIcon sx={{ mr: 1, fontSize: 18 }} />
                    Category & Subcategory
                  </Typography>
                  <Breadcrumbs>
                    <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                      {selectedCategory.name}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {selectedSubcategory.name}
                    </Typography>
                  </Breadcrumbs>
                  {!isEditing && (
                    <Button
                      size="small"
                      onClick={handleChangeSubcategory}
                      sx={{ mt: 1 }}
                    >
                      Change Category
                    </Button>
                  )}
                </Paper>
              )}

              {/* Show form only after subcategory selection for new offers */}
              {(isEditing || step === 'form') && (
                <>
                  <TextField
                    label="Title"
                    value={formData.title}
                    onChange={handleChange('title')}
                    required
                    fullWidth
                    placeholder="Enter a compelling offer title"
                  />

                  <BrandSelector
                    value={formData.brand}
                    onChange={(brand) => setFormData(prev => ({ ...prev, brand }))}
                    label="Associated Brand (Optional)"
                    helperText="Select the brand this offer is associated with"
                  />

                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={handleChange('description')}
                    required
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Describe what makes this offer special..."
                  />

                  <DateTimePicker
                    label="Expiration Date & Time"
                    value={formData.expirationDate}
                    onChange={handleDateChange}
                    slots={{
                      textField: TextField
                    }}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        helperText: "When does this offer expire?"
                      }
                    }}
                    minDateTime={dayjs()}
                  />

                  <TextField
                    label="Purchase URL"
                    value={formData.purchaseUrl}
                    onChange={handleChange('purchaseUrl')}
                    required
                    fullWidth
                    placeholder="https://example.com/offer"
                    helperText="Where can users claim this offer?"
                  />

                  <TextField
                    label="Blog Link (Optional)"
                    value={formData.blogLink}
                    onChange={handleChange('blogLink')}
                    fullWidth
                    placeholder="https://example.com/blog-post"
                    helperText="Link to a related blog post or article"
                  />

                  <TextField
                    label="Featured Image URL (Optional)"
                    value={formData.featuredImage}
                    onChange={handleChange('featuredImage')}
                    fullWidth
                    placeholder="https://example.com/image.jpg"
                    helperText="URL to an image that represents this offer"
                  />

                  {/* Template-specific fields */}
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Offer Details
                    </Typography>
                  </Divider>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                      label="Original Price (Optional)"
                      value={formData.originalPrice}
                      onChange={handleChange('originalPrice')}
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="99.99"
                      helperText="Original price before discount"
                    />

                    <TextField
                      label="Final Price (Optional)"
                      value={formData.finalPrice}
                      onChange={handleChange('finalPrice')}
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="79.99"
                      helperText="Final price after discount"
                    />

                    <TextField
                      label="Discount Percentage (Optional)"
                      value={formData.discountPercentage}
                      onChange={handleChange('discountPercentage')}
                      type="number"
                      inputProps={{ min: 0, max: 100 }}
                      placeholder="20"
                      helperText="Discount percentage (0-100)"
                    />

                    <TextField
                      label="Discount Amount (Optional)"
                      value={formData.discountAmount}
                      onChange={handleChange('discountAmount')}
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      placeholder="20.00"
                      helperText="Fixed discount amount"
                    />
                  </Box>

                  <TextField
                    label="Features (Optional)"
                    value={featuresInput}
                    onChange={handleFeaturesChange}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Feature 1, Feature 2, Feature 3"
                    helperText="Comma-separated list of features"
                  />

                  <TextField
                    label="Tags (Optional)"
                    value={tagsInput}
                    onChange={handleTagsChange}
                    fullWidth
                    placeholder="tag1, tag2, tag3"
                    helperText="Comma-separated list of tags"
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                      label="Call to Action (Optional)"
                      value={formData.callToAction}
                      onChange={handleChange('callToAction')}
                      placeholder="Get This Deal"
                      helperText="Button text for the offer"
                    />

                    <TextField
                      label="Urgency Text (Optional)"
                      value={formData.urgencyText}
                      onChange={handleChange('urgencyText')}
                      placeholder="Limited Time Offer"
                      helperText="Text to create urgency"
                    />
                  </Box>

                  <FormControl fullWidth>
                    <InputLabel id="traffic-sources-label">Traffic Sources Allowed (Optional)</InputLabel>
                    <Select
                      labelId="traffic-sources-label"
                      label="Traffic Sources Allowed (Optional)"
                      value={formData.trafficSources || []}
                      onChange={(event) => setFormData(prev => ({ ...prev, trafficSources: event.target.value }))}
                      multiple
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="Social Media">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Social Media</Typography>
                          <Typography variant="caption" color="text.secondary">(Facebook, Instagram, Twitter, etc.)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Email Marketing">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Email Marketing</Typography>
                          <Typography variant="caption" color="text.secondary">(Newsletters, campaigns)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Paid Ads">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Paid Ads</Typography>
                          <Typography variant="caption" color="text.secondary">(Google Ads, Facebook Ads, etc.)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Organic Search">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Organic Search</Typography>
                          <Typography variant="caption" color="text.secondary">(SEO, Google search)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Direct Traffic">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Direct Traffic</Typography>
                          <Typography variant="caption" color="text.secondary">(Direct URL visits)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Affiliate Marketing">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Affiliate Marketing</Typography>
                          <Typography variant="caption" color="text.secondary">(Partner websites)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Content Marketing">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Content Marketing</Typography>
                          <Typography variant="caption" color="text.secondary">(Blogs, articles)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Influencer Marketing">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Influencer Marketing</Typography>
                          <Typography variant="caption" color="text.secondary">(Influencer collaborations)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Retargeting">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Retargeting</Typography>
                          <Typography variant="caption" color="text.secondary">(Remarketing campaigns)</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Mobile Apps">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Mobile Apps</Typography>
                          <Typography variant="caption" color="text.secondary">(App-based traffic)</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                    <FormHelperText>Select which traffic sources are allowed for this offer</FormHelperText>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.published}
                        onChange={handleChange('published')}
                        color="primary"
                      />
                    }
                    label="Publish immediately"
                    sx={{ mt: 2 }}
                  />

                  <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={submitLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={submitLoading || (!isEditing && step !== 'form')}
                      sx={{ minWidth: 150 }}
                    >
                      {submitLoading ? 'Saving...' : (isEditing ? 'Update Offer' : 'Create Offer')}
                    </Button>
                    
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/offers')}
                      disabled={submitLoading}
                      data-cancel="true"
                    >
                      Cancel
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Subcategory Selector Dialog */}
        <SubcategorySelector
          open={showSubcategorySelector}
          onClose={() => setShowSubcategorySelector(false)}
          onSelect={handleSubcategorySelect}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default OfferEditor; 